import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { AnalyticsInsightsService } from './analytics-insights.service';
import { IdentityService } from '../../identity/identity.service';
import { ClientContactsService } from 'src/app/services/mercury/client-contacts/client-contacts.service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { DropdownService } from 'src/app/services/dropdown/dropdown.service';
import { filter, map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class AnalyticsInsightsListeners {
	private identity = inject(IdentityService);
	private clientContactsService = inject(ClientContactsService);
	private analyticsInsightsService = inject(AnalyticsInsightsService);
	private dropdownService = inject(DropdownService);
	private environmentService = inject(EnvironmentService);

	private subscriptions: Subscription;
	private userID = '';
	private dropdownIsLoaded = new BehaviorSubject<boolean>(false);

	public initialize(): void {
		this.analyticsInsightsService.initialize();
		this.registerSubscribers();
	}

	private registerSubscribers(): void {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}

		this.subscriptions = new Subscription();

		this.subscriptions.add(
			this.environmentService.environment$.subscribe((environment) => {
				if (!environment) return;

				this.analyticsInsightsService.addTelemetryBaseData(
					'Environment',
					environment
				);
			})
		);

		this.subscriptions.add(
			this.identity.userData$.subscribe((user) => {
				if (!user) return;

				this.userID = user.sub;
				this.analyticsInsightsService.setAuthentication(user.sub);
				this.analyticsInsightsService.addTelemetryBaseData(
					'UserID',
					user.sub
				);
			})
		);

		this.subscriptions.add(
			this.dropdownService.isLoaded$.subscribe(async (loaded) => {
				if (!loaded) return;

				const profile = await this.clientContactsService.loadAsync(
					this.userID
				);
				if (!profile) return;

				if (profile.country)
					this.analyticsInsightsService.addTelemetryBaseData(
						'Country',
						profile.country
					);

				if (
					profile.extension_7b56b84fc1494c1585ff22022dfacb63_CompanyCode
				) {
					this.analyticsInsightsService.addTelemetryBaseData(
						'CompanyCode',
						profile.extension_7b56b84fc1494c1585ff22022dfacb63_CompanyCode
					);
					const companyData =
						this.dropdownService.dropdownClients.find(
							(c: any) =>
								c.companyCode ===
								profile.extension_7b56b84fc1494c1585ff22022dfacb63_CompanyCode
						);

					if (companyData) {
						this.analyticsInsightsService.addTelemetryBaseData(
							'CompanyName',
							companyData.companyName
						);
						this.analyticsInsightsService.addTelemetryBaseData(
							'Operation',
							companyData.operation
						);
					}
				}
				this.dropdownIsLoaded.next(true);
			})
		);

		this.subscriptions.add(
			combineLatest([
				this.environmentService.environment$,
				this.identity.userData$,
				this.dropdownService.isLoaded$,
				this.dropdownIsLoaded
			])
				.pipe(
					map(
						([environment, userData, isLoaded, dropdownIsLoaded]) =>
							environment &&
							userData &&
							isLoaded &&
							dropdownIsLoaded
					),
					filter((loaded) => loaded)
				)
				.subscribe((value) => {
					this.analyticsInsightsService.updateIsLoaded(value);
				})
		);
	}
}
